import cookie from 'js/lib/cookie';
import store from 'js/lib/coursera.store';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import courseraPlusExperiments from 'bundles/epic/clients/courseraPlus';
import type SubscriptionsV1 from 'bundles/naptimejs/resources/subscriptions.v1';

export const getIsLearnerImpressedInIndiaTiersEpic = () => {
  // Recording impressions here for monitoring purposes
  return courseraPlusExperiments.get('isLearnerImpressedInIndiaTiersEpic');
};

export const WELCOME_MODAL_KEY = 'subscription_tiers_welcome_modal';

export const UPGRADE_MESSAGE_KEY = 'subscription_tiers_upgrade_message';

const getHasSeen = (cookieKey: string) => {
  const cookieValue = cookie.get(cookieKey);
  return !!cookieValue?.includes(tupleToStringKey([user.get().id, cookieKey]));
};

export const getHasSeenSubscriptionTiersWelcomeModal = () => getHasSeen(WELCOME_MODAL_KEY);

export const getHasSeenSubscriptionTiersUpgradeMessage = () => getHasSeen(UPGRADE_MESSAGE_KEY);

const setHasSeen = (cookieKey: string) => {
  const value = cookie.get(cookieKey) ?? '';
  cookie.set(cookieKey, `${value},${tupleToStringKey([user.get().id, cookieKey])}`);
};

export const setHasSeenSubscriptionTiersWelcomeModal = () => setHasSeen(WELCOME_MODAL_KEY);

export const setHasSeenSubscriptionTiersUpgradeMessage = () => setHasSeen(UPGRADE_MESSAGE_KEY);

export const isValidCourseraLiteSubscription = (subscription: SubscriptionsV1) => {
  const { isCourseraLiteSubscription, isFreeTrial, isActive, isCancelled } = subscription;
  // Adding the isCancelled check here because a learner can still have access to Lite
  // for the remainder of their billing cycle days after they cancelled the subscription
  return isCourseraLiteSubscription && (isFreeTrial || isActive || isCancelled);
};

const REDIRECT_URL_AFTER_LITE_UPGRADE_KEY = 'redirectUrlAfterUpgrade';
const HAS_UPGRADED_SUCCESSFULLY_KEY = 'upgradeSuccessful';
const INDIA_LITE_TO_PLUS_UPGRADE_DATA_KEY = 'indiaLiteToPlusUpgradeData';

export const markUpgradeAsSuccessful = () => {
  store.set(HAS_UPGRADED_SUCCESSFULLY_KEY, `${user.get().id}`);
};

const getIsUpgradeSuccessful = () => {
  const isUpgradeSuccessful = store.get(HAS_UPGRADED_SUCCESSFULLY_KEY) || undefined;
  return isUpgradeSuccessful?.toString() === `${user.get()?.id}`;
};

export const saveRedirectUrlAfterUpgrade = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.pathname : undefined;
  store.set(REDIRECT_URL_AFTER_LITE_UPGRADE_KEY, currentUrl);
};

export const getRedirectUrlAfterUpgrade = () => {
  const redirectUrl = store.get(REDIRECT_URL_AFTER_LITE_UPGRADE_KEY) || undefined;
  return getIsUpgradeSuccessful() ? redirectUrl : undefined;
};

export const removeRedirectUrlAfterUpgrade = () => {
  store.remove(HAS_UPGRADED_SUCCESSFULLY_KEY);
  store.remove(REDIRECT_URL_AFTER_LITE_UPGRADE_KEY);
  store.remove(INDIA_LITE_TO_PLUS_UPGRADE_DATA_KEY);
};

export const redactPayPalEmail = (email: string) => {
  const [first, last] = email.split('@');
  const firstRedacted = first.slice(0, 2) + '*'.repeat(first.length - 2);
  return `${firstRedacted}@${last}`;
};

type IndiaUpgradeData = { liteSubscriptionId: string; s12nId?: string; courseId?: string; userId: string };

export const getIndiaTiersUpgradeData = (): IndiaUpgradeData | undefined => {
  const rawData = store.get(INDIA_LITE_TO_PLUS_UPGRADE_DATA_KEY);
  return rawData ? JSON.parse(rawData) : undefined;
};

export const setIndiaTiersUpgradeData = (data: { liteSubscriptionId: string; s12nId?: string; courseId?: string }) => {
  if (data) {
    store.set(INDIA_LITE_TO_PLUS_UPGRADE_DATA_KEY, JSON.stringify({ ...data, userId: user.get().id }));
  }
};
